import React from 'react';
import {
    Box,
    Typography,
    Card,
    CardContent,
    IconButton,
    useTheme
} from '@mui/material';
import { ReactComponent as ArrowUpwordCorner } from '../../images/ArrowCorner.svg';

interface WSDashboardCardProps {
    title: string;
    value: string | number;
    percentage?: string;
    icon: React.ReactNode;
    bottomIcon?: React.ReactNode;
    onClick?: () => void;
    percentageColor?: string;
    bgColor?: string;
    height?: string | number;
    // Keeps the card clickable but without the arrow button
    hideArrow?: boolean;
}

const WSDashboardCard: React.FC<WSDashboardCardProps> = ({
    title,
    value,
    percentage,
    icon,
    onClick,
    percentageColor = '#098963',
    bgColor = '#E7F6F2',
    bottomIcon,
    height,
    hideArrow = false,
}) => {
    const theme = useTheme();

    return (
        <Card
            // The whole card opens its page when onClick is given, not just the arrow
            onClick={onClick}
            onKeyDown={onClick ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); } } : undefined}
            role={onClick ? 'button' : undefined}
            tabIndex={onClick ? 0 : undefined}
            aria-label={onClick ? `${title}: ${value}` : undefined}
            sx={{
                borderRadius: '16px',
                boxShadow: '0px 4px 16px rgba(0, 0, 0, 0.05)',
                backgroundColor: '#fff',
                position: 'relative',
                height: height || 'auto',
                display: 'flex',
                flexDirection: 'column',
                cursor: onClick ? 'pointer' : 'default',
                '&:focus-visible': { outline: '2px solid #098963', outlineOffset: 2 },
                '&:hover': {
                   // transform: 'translateY(-4px)',
                    boxShadow: '0px 8px 24px rgba(0, 0, 0, 0.1)',
                    border:'1px solid #CEEEE5'
                },
            }}
        >
            <CardContent
                sx={{
                    paddingBottom: '16px !important',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    flexGrow: 1, // ensures content takes full height
                }}
            >
                {/* Title and Icon */}
                <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                    <Box display="flex" alignItems="center" gap={1}>
                        {icon}
                        <Typography variant="subtitle2" color="text.secondary">
                            {title}
                        </Typography>
                    </Box>
                </Box>

                {/* Value */}
                <Typography
                    variant="h4"
                    fontWeight="bold"
                    mb={1}
                    sx={{ flexGrow: 1, display: 'flex', alignItems: 'center' }}
                >
                    {value}
                </Typography>

                {/* Percentage and arrow */}
                <Box display="flex" alignItems="center" justifyContent="space-between">
                    {percentage ? (
                    <Box
                        display="flex"
                        alignItems="center"
                        gap={0.5}
                        px={1}
                        py={0.5}
                        borderRadius="12px"
                        bgcolor={bgColor}
                        color={percentageColor}
                        fontSize="0.75rem"
                        fontWeight="500"
                    >
                        {bottomIcon && bottomIcon}
                        {percentage}
                    </Box>
                    ) : <Box />}

                    {onClick && !hideArrow && (
                        <IconButton size="small" tabIndex={-1} aria-hidden>
                            <ArrowUpwordCorner style={{ width: '100%', height: '100%' }} />
                        </IconButton>
                    )}
                </Box>
            </CardContent>
        </Card>
    );
};

export default WSDashboardCard;
